import Banner from '@carvana/ds-banner';
import Spinner from '@carvana/ds-spinner';
import { Elements, ExpressCheckoutElement } from '@stripe/react-stripe-js';
import type { StripeElementsOptions, StripeExpressCheckoutElementOptions } from '@stripe/stripe-js';
import classNames from 'classnames';
import React from 'react';
import styles from './digital-wallets.module.css';
import { getStripe } from './get-stripe';
import { useDigitalWallets } from './use-digital-wallets';
import type { DigitalWallet } from '../../../services/purchase-payments-api.types';
import { DigitalWalletUnavailableBanner } from '../digital-wallet-unavailable-banner';

const stripeElementsOptions = {
  mode: 'setup',
  currency: 'usd',
  paymentMethodTypes: ['card'],
  setupFutureUsage: 'off_session',
  appearance: {
    variables: {
      borderRadius: '100px'
    }
  }
} satisfies StripeElementsOptions;

const getStripeExpressCheckoutElementOptions = (digitalWallet: DigitalWallet | undefined) =>
  ({
    paymentMethods: {
      applePay: digitalWallet === 'GooglePay' ? 'never' : 'always',
      googlePay: digitalWallet === 'ApplePay' ? 'never' : 'always',
      amazonPay: 'never',
      klarna: 'never',
      link: 'never',
      paypal: 'never'
    },
    paymentMethodOrder: ['apple_pay', 'google_pay'],
    buttonType: {
      applePay: 'plain',
      googlePay: 'plain'
    },
    buttonTheme: {
      applePay: 'black',
      googlePay: 'black'
    },
    layout: {
      maxColumns: 1,
      overflow: 'never'
    },
    buttonHeight: 50
  }) satisfies StripeExpressCheckoutElementOptions;

interface DigitalWalletsProps {
  digitalWallet: DigitalWallet | undefined;
  isCardEligible: boolean;
  editPaymentMethod: () => void;
  onSupportChange: (value: boolean) => void;
  onCardAdded: () => Promise<{ isCardEligible: boolean }>;
  onSetupConfirmed: () => Promise<boolean>;
  onError: () => void;
}

const DigitalWallets = ({
  digitalWallet,
  isCardEligible,
  editPaymentMethod,
  onSupportChange,
  onCardAdded,
  onSetupConfirmed,
  onError
}: DigitalWalletsProps) => {
  const {
    isReady,
    isSupported,
    isSaving,
    error,
    onReady,
    onLoadError,
    onClick,
    onCancel,
    onConfirm
  } = useDigitalWallets({
    digitalWallet,
    onSupportChange,
    onCardAdded,
    onSetupConfirmed,
    onError
  });

  if (!isCardEligible) {
    return (
      <DigitalWalletUnavailableBanner
        reason="CardIneligible"
        digitalWallet={digitalWallet}
        editPaymentMethod={editPaymentMethod}
      />
    );
  }

  if (isReady && !isSupported) {
    return (
      <DigitalWalletUnavailableBanner
        reason="UnsupportedDevice"
        digitalWallet={digitalWallet}
        editPaymentMethod={editPaymentMethod}
      />
    );
  }

  const isLoading = !isReady || isSaving;

  return (
    <>
      {error && (
        <div className={styles.errorBannerContainer}>
          <Banner open type="critical" icon align="left">
            <Banner.Headline>{error.headline}</Banner.Headline>
            <Banner.Message>{error.message}</Banner.Message>
          </Banner>
        </div>
      )}

      {isLoading && (
        <div className={styles.loadingContainer}>
          <Spinner size={36} />
        </div>
      )}

      <div className={classNames(styles.container, isLoading && styles.hidden)}>
        <ExpressCheckoutElement
          options={getStripeExpressCheckoutElementOptions(digitalWallet)}
          onReady={onReady}
          onLoadError={onLoadError}
          onClick={onClick}
          onCancel={onCancel}
          onConfirm={onConfirm}
          data-analytics-id="pp_complete_purchase_digital_wallets_element"
        />
      </div>
    </>
  );
};

const DigitalWalletsWrapper = (props: DigitalWalletsProps) => (
  <Elements stripe={getStripe()} options={stripeElementsOptions}>
    <DigitalWallets {...props} />
  </Elements>
);

export { DigitalWalletsWrapper as DigitalWallets };
